import type { LineageGraph, LineageNode } from './types';

export function getNode(g: LineageGraph, id: string): LineageNode | null {
  return g.nodes[id] ?? null;
}

export function findAncestors(g: LineageGraph, id: string, maxDepth = 100): LineageNode[] {
  const result: LineageNode[] = [];
  const visited = new Set<string>([id]);
  const queue: Array<{ id: string; depth: number }> = [{ id, depth: 0 }];
  while (queue.length > 0) {
    const { id: cur, depth } = queue.shift()!;
    if (depth >= maxDepth) continue;
    const node = g.nodes[cur];
    if (!node) continue;
    for (const pid of node.parentIds) {
      if (pid !== null && !visited.has(pid)) {
        visited.add(pid);
        const pnode = g.nodes[pid];
        if (pnode) {
          result.push(pnode);
          queue.push({ id: pid, depth: depth + 1 });
        }
      }
    }
  }
  return result;
}

export function findDescendants(g: LineageGraph, id: string): LineageNode[] {
  const result: LineageNode[] = [];
  const visited = new Set<string>([id]);
  const stack = [id];
  while (stack.length > 0) {
    const cur = stack.pop()!;
    const node = g.nodes[cur];
    if (!node) continue;
    for (const childId of node.childrenIds) {
      if (!visited.has(childId)) {
        visited.add(childId);
        const cnode = g.nodes[childId];
        if (cnode) {
          result.push(cnode);
          stack.push(childId);
        }
      }
    }
  }
  return result;
}

export function findSiblings(g: LineageGraph, id: string): LineageNode[] {
  const node = g.nodes[id];
  if (!node) return [];
  const seen = new Set<string>();
  const siblings: LineageNode[] = [];
  for (const pid of node.parentIds) {
    if (pid === null) continue;
    const parent = g.nodes[pid];
    if (!parent) continue;
    for (const childId of parent.childrenIds) {
      if (childId === id || seen.has(childId)) continue;
      const sib = g.nodes[childId];
      if (sib) {
        seen.add(childId);
        siblings.push(sib);
      }
    }
  }
  return siblings;
}
